// Phase 10B "AutoMind 2050" — Operations Command Center.
//
// Premium, data-only dashboard. Every tile is backed by a real
// `/api/dashboard/*` endpoint — no mocks, no Math.random fallbacks.
// When a fetch returns an empty payload or fails, the section renders
// an EmptyState rather than making numbers up.
//
// Design tokens (kept tight):
//   ink-900  #18181B   primary series / labels
//   ink-500  #71717A   secondary series
//   ink-300  #D4D4D8   low-intensity bars
//   ink-200  #E4E4E7   tertiary series / borders
//   ink-100  #F4F4F5   chart grid
//   gold-500 #C9A568   single accent; used sparingly

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import { dashboardApi, tasksApi } from '../api';
import { PageHeader } from '../components/ui/page-header';
import { MetricCard } from '../components/ui/metric-card';
import { DataCard } from '../components/ui/data-card';
import { ChartCard } from '../components/ui/chart-card';
import { SectionGrid } from '../components/ui/section-grid';
import { SubTabBar } from '../components/ui/sub-tab-bar';
import { EmptyState } from '../components/ui/empty-state';
import { StatusPill } from '../components/ui/status-pill';
import { PriorityBadge } from '../components/ui/priority-badge';

// ---- palette ---------------------------------------------------------------
const INK_900 = '#18181B';
const INK_500 = '#71717A';
const INK_300 = '#D4D4D8';
const INK_200 = '#E4E4E7';
const INK_100 = '#F4F4F5';
const GOLD = '#C9A568';

// ---- range -> days mapping -------------------------------------------------
// SubTabs map to API day windows. `today` collapses to a 1-day window for
// summary, but charts that look smoother over a week still pass 7.
const RANGE_DAYS = { today: 1, '7d': 7, '30d': 30 };
const RANGE_FUNNEL_DAYS = { today: 1, '7d': 7, '30d': 30 };

// ---- tiny formatters -------------------------------------------------------

function formatPercent(v) {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(Number(v) * 1000) / 10; // one decimal
}

function truncate(str, n) {
  if (!str) return '';
  const s = String(str);
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 8 ? s.slice(0, 8) : s;
}

// "in 12m" / "overdue 3h" / "in 2d". Tolerant of missing / bad dates.
function relativeTime(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  let label;
  if (mins < 60) label = `${mins}m`;
  else if (mins < 60 * 24) label = `${Math.round(mins / 60)}h`;
  else label = `${Math.round(mins / (60 * 24))}d`;
  return past ? `overdue ${label}` : `in ${label}`;
}

// "MMM D" — avoid bringing in a date library for one format.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatShortDate(iso) {
  if (!iso) return '';
  // "YYYY-MM-DD" from the call-volume endpoint. Parse manually to stay
  // timezone-stable.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = MONTHS[d.getMonth()];
  const day = d.getDate();
  const hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  const h12 = ((hh + 11) % 12) + 1;
  const ampm = hh < 12 ? 'am' : 'pm';
  return `${month} ${day}, ${h12}:${mm}${ampm}`;
}

function sentimentTone(sentiment) {
  const s = String(sentiment || '').toLowerCase();
  if (s === 'positive') return 'success';
  if (s === 'negative') return 'danger';
  if (s === 'mixed') return 'warning';
  return 'neutral';
}

function appointmentTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'confirmed') return 'success';
  if (s === 'scheduled') return 'info';
  if (s === 'cancelled') return 'danger';
  return 'neutral';
}

// ---- shared small hook -----------------------------------------------------
// Generic per-section fetch hook. Keeps each panel self-contained — a
// failure in one panel doesn't poison the rest of the page.
function useSectionFetch(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const out = await fn();
      setData(out);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { data, loading, error, refetch: run };
}

// ---- Call volume -----------------------------------------------------------

function CallVolumeChart({ days }) {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.callVolume({ days }),
    [days]
  );

  const points = (data?.points || []).map((p) => ({
    ...p,
    label: formatShortDate(p.date),
  }));
  const hasData = points.some((p) => (p.total || 0) > 0);

  if (error) {
    return (
      <EmptyState
        title="No data yet"
        description="Unable to load right now."
      />
    );
  }
  if (loading) {
    return <div className="h-[220px] w-full skeleton" />;
  }
  if (!hasData) {
    return (
      <EmptyState
        title="No data yet"
        description="Data will appear once calls are processed."
      />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={INK_100} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: INK_500 }}
          tickLine={false}
          axisLine={{ stroke: INK_200 }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: INK_500 }}
          tickLine={false}
          axisLine={{ stroke: INK_200 }}
          width={32}
          allowDecimals={false}
        />
        <Tooltip
          cursor={{ stroke: INK_300, strokeWidth: 1 }}
          contentStyle={{
            background: '#FFFFFF',
            border: `1px solid ${INK_200}`,
            borderRadius: 6,
            fontSize: 12,
          }}
          labelStyle={{ color: INK_900, fontWeight: 500 }}
        />
        <Area
          type="monotone"
          dataKey="total"
          name="Total"
          stroke={INK_900}
          strokeWidth={1.5}
          fill={INK_900}
          fillOpacity={0.06}
          dot={false}
          activeDot={{ r: 3, stroke: INK_900, fill: '#FFFFFF', strokeWidth: 1.5 }}
        />
        <Area
          type="monotone"
          dataKey="completed"
          name="Completed"
          stroke={GOLD}
          strokeWidth={1.5}
          fill={GOLD}
          fillOpacity={0.10}
          dot={false}
          activeDot={{ r: 3, stroke: GOLD, fill: '#FFFFFF', strokeWidth: 1.5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---- Funnel (custom horizontal bars) --------------------------------------

const FUNNEL_ORDER = [
  { key: 'attempted', label: 'Attempted' },
  { key: 'connected', label: 'Connected' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'booked', label: 'Booked' },
];

function FunnelChart({ days }) {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.funnel({ days }),
    [days]
  );
  const stages = data?.stages || [];
  const lookup = Object.fromEntries(stages.map((s) => [s.label, s.count]));
  const rows = FUNNEL_ORDER.map((s) => ({
    ...s,
    count: Number(lookup[s.key] || 0),
  }));
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const hasData = max > 0;

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return <div className="h-[220px] w-full skeleton" />;
  }
  if (!hasData) {
    return (
      <EmptyState
        title="No data yet"
        description="Data will appear once calls are processed."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      {rows.map((r, idx) => {
        const pct = max > 0 ? Math.max(4, (r.count / max) * 100) : 0;
        // The last (booked) stage gets the gold accent — single point of
        // emphasis per chart, matches the design rule.
        const fill = idx === rows.length - 1 ? GOLD : INK_500;
        return (
          <div key={r.key}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="font-medium text-ink-700 dark:text-ink-200">{r.label}</span>
              <span className="nums text-ink-900 dark:text-ink-50 font-semibold">
                {r.count}
              </span>
            </div>
            <div className="h-2 w-full rounded bg-ink-100 dark:bg-ink-800/60 overflow-hidden">
              <div
                className="h-full rounded transition-all duration-700"
                style={{ width: `${pct}%`, background: fill }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Intent distribution ---------------------------------------------------

const INTENT_ROWS = [
  { key: 'high', label: 'High intent', color: INK_900 },
  { key: 'medium', label: 'Medium intent', color: INK_500 },
  { key: 'low', label: 'Low intent', color: INK_300 },
];

function IntentChart({ days }) {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.intentDistribution({ days }),
    [days]
  );
  const buckets = data?.buckets || [];
  const byLabel = Object.fromEntries(buckets.map((b) => [b.label, b.count]));
  const total = Number(data?.total || 0);
  const rows = INTENT_ROWS.map((r) => ({
    ...r,
    count: Number(byLabel[r.key] || 0),
  }));
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return <div className="h-[220px] w-full skeleton" />;
  }
  if (total === 0) {
    return (
      <EmptyState
        title="No data yet"
        description="Data will appear once calls are processed."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      {rows.map((r) => {
        const pct = max > 0 ? Math.max(4, (r.count / max) * 100) : 0;
        return (
          <div key={r.key}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="font-medium text-ink-700 dark:text-ink-200">{r.label}</span>
              <span className="nums text-ink-900 dark:text-ink-50 font-semibold">
                {r.count}
              </span>
            </div>
            <div className="h-2 w-full rounded bg-ink-100 dark:bg-ink-800/60 overflow-hidden">
              <div
                className="h-full rounded transition-all duration-700"
                style={{ width: `${pct}%`, background: r.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Objections list -------------------------------------------------------

function ObjectionsList({ days }) {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.objections({ days }),
    [days]
  );
  const items = (data?.items || []).slice(0, 6);
  const max = items.reduce((m, r) => Math.max(m, r.count || 0), 0);

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return <div className="h-[220px] w-full skeleton" />;
  }
  if (!items.length) {
    return (
      <EmptyState
        title="No objections recorded"
        description="Data will appear once calls are analysed."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 py-2">
      {items.map((row) => {
        const pct = max > 0 ? Math.max(4, (row.count / max) * 100) : 0;
        return (
          <div key={row.objection_type} className="flex items-center gap-3">
            <div className="w-36 text-xs text-ink-700 dark:text-ink-200 truncate">
              {truncate(row.objection_type, 18)}
            </div>
            <div className="flex-1 h-1.5 rounded bg-ink-100 dark:bg-ink-800/60 overflow-hidden">
              <div
                className="h-full rounded transition-all duration-700"
                style={{ width: `${pct}%`, background: INK_500 }}
              />
            </div>
            <div className="w-10 text-right text-xs nums text-ink-900 dark:text-ink-50 font-semibold">
              {row.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Hot leads -------------------------------------------------------------

function HotLeadsPanel({ onOpenCall }) {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.hotLeads({ limit: 5 }),
    []
  );
  const items = data?.items || [];

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-2/3 skeleton" />
        <div className="h-4 w-1/2 skeleton" />
        <div className="h-4 w-3/4 skeleton" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <EmptyState
        title="No hot leads yet"
        description="Top-scored analyses will appear here as calls are processed."
      />
    );
  }

  return (
    <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60 -mx-2">
      {items.map((row, idx) => {
        const idKey = row.id || row.call_id || row.lead_id || `hot-${idx}`;
        const display = shortId(row.call_id || row.lead_id || row.id);
        const score = Math.round(Number(row.score || 0));
        return (
          <li
            key={idKey}
            className="row-2050 flex items-center gap-3 px-2 py-3"
          >
            <div className="text-lg font-semibold nums text-ink-900 dark:text-ink-50 w-10 text-right">
              {score}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-ink-700 dark:text-ink-200">
                  {display}
                </span>
                {row.sentiment && (
                  <StatusPill
                    tone={sentimentTone(row.sentiment)}
                    label={String(row.sentiment)}
                  />
                )}
              </div>
              <div className="mt-1 text-xs text-ink-500 truncate">
                {row.next_best_action
                  ? `Next best: ${row.next_best_action}`
                  : 'No recommended action'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpenCall && onOpenCall(row)}
              className="btn-2050 btn-ghost text-xs"
            >
              View
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---- Urgent tasks ----------------------------------------------------------

function UrgentTasksPanel() {
  const { data, loading, error, refetch } = useSectionFetch(
    () => dashboardApi.urgentTasks({ limit: 8 }),
    []
  );
  const items = data?.items || [];
  const [busyId, setBusyId] = useState(null);

  const handleAction = async (id, action) => {
    if (!id || busyId) return;
    setBusyId(id);
    try {
      if (action === 'complete') await tasksApi.complete(id);
      else if (action === 'dismiss') await tasksApi.dismiss(id);
      await refetch();
    } catch (_) {
      // Swallow — a second click will retry naturally.
    } finally {
      setBusyId(null);
    }
  };

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-2/3 skeleton" />
        <div className="h-4 w-1/2 skeleton" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <EmptyState
        title="No urgent tasks"
        description="Open high-priority items will appear here."
      />
    );
  }

  return (
    <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60 -mx-2">
      {items.map((row, idx) => {
        const id = row.id || `urgent-${idx}`;
        const rel = relativeTime(row.due_at);
        return (
          <li
            key={id}
            className="row-2050 flex items-center gap-3 px-2 py-3"
          >
            <PriorityBadge priority={row.priority} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
                  {String(row.task_type || 'task').replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-sm text-ink-900 dark:text-ink-50 truncate">
                {row.title || row.description || 'Untitled task'}
              </div>
            </div>
            <div className="text-xs text-ink-500 w-24 text-right nums">
              {rel || '—'}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="btn-2050 btn-ghost text-xs"
                disabled={busyId === row.id}
                onClick={() => handleAction(row.id, 'complete')}
              >
                Complete
              </button>
              <button
                type="button"
                className="btn-2050 btn-ghost text-xs"
                disabled={busyId === row.id}
                onClick={() => handleAction(row.id, 'dismiss')}
              >
                Dismiss
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---- Upcoming appointments -------------------------------------------------

function UpcomingAppointments() {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.upcomingAppointments({ days: 7, limit: 8 }),
    []
  );
  const items = data?.items || [];

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-2/3 skeleton" />
        <div className="h-4 w-1/2 skeleton" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <EmptyState
        title="No upcoming appointments"
        description="Confirmed and scheduled appointments for the next 7 days will appear here."
      />
    );
  }

  return (
    <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60 -mx-2">
      {items.map((row, idx) => {
        const id = row.id || `appt-${idx}`;
        return (
          <li
            key={id}
            className="row-2050 flex items-center gap-3 px-2 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-ink-900 dark:text-ink-50 truncate">
                {row.customer_name || 'Customer'}
              </div>
              <div className="text-xs text-ink-500 truncate">
                {row.appointment_type
                  ? String(row.appointment_type).replace(/_/g, ' ')
                  : 'appointment'}
              </div>
            </div>
            <div className="text-xs text-ink-700 dark:text-ink-200 nums w-36 text-right">
              {formatDateTime(row.start_time)}
            </div>
            {row.status && (
              <StatusPill
                tone={appointmentTone(row.status)}
                label={String(row.status)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---- Reminder activity -----------------------------------------------------

const REMINDER_ORDER = [
  { key: 'sent', label: 'Sent' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'failed', label: 'Failed' },
  { key: 'not_scheduled', label: 'Not scheduled' },
  { key: 'cancelled', label: 'Cancelled' },
];

function ReminderActivity() {
  const { data, loading, error } = useSectionFetch(
    () => dashboardApi.reminderActivity({ days: 7 }),
    []
  );
  const buckets = data?.buckets || {};
  const total = REMINDER_ORDER.reduce(
    (sum, r) => sum + Number(buckets[r.key] || 0),
    0
  );

  if (error) {
    return (
      <EmptyState title="No data yet" description="Unable to load right now." />
    );
  }
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-2/3 skeleton" />
        <div className="h-4 w-1/2 skeleton" />
      </div>
    );
  }
  if (total === 0) {
    return (
      <EmptyState
        title="No reminders yet"
        description="Reminder activity will appear once appointments are booked."
      />
    );
  }

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
      {REMINDER_ORDER.map((r) => (
        <div
          key={r.key}
          className="flex items-baseline justify-between border-b border-ink-200/60 dark:border-ink-800/60 pb-2"
        >
          <dt className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
            {r.label}
          </dt>
          <dd className="text-lg font-semibold nums text-ink-900 dark:text-ink-50">
            {Number(buckets[r.key] || 0)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---- Page ------------------------------------------------------------------

const Dashboard = ({ setCurrentPage } = {}) => {
  // Range pill controls the chart windows. Summary endpoint is "now"-shaped
  // and ignores `days`; charts and funnel respond to it.
  const [range, setRange] = useState('7d');
  const days = RANGE_DAYS[range] ?? 7;
  const funnelDays = RANGE_FUNNEL_DAYS[range] ?? 14;

  // Summary is the only piece rendered directly in the page body; the
  // rest of the panels own their own fetching to stay independent.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSummaryLoading(true);
      setSummaryError(false);
      try {
        const out = await dashboardApi.summary();
        if (!cancelled) setSummary(out);
      } catch (_) {
        if (!cancelled) setSummaryError(true);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // Sparkline for "Calls today" comes from the call-volume endpoint over
  // the last 7 days. Backed by real data — null/empty arrays are fine and
  // MetricCard simply renders without the spark.
  const [sparkData, setSparkData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await dashboardApi.callVolume({ days: 7 });
        if (cancelled) return;
        const pts = (out?.points || []).map((p) => Number(p.total || 0));
        setSparkData(pts);
      } catch (_) {
        // leave null — the card just won't show the sparkline
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const pct = formatPercent(summary?.connect_rate);

  const handleOpenCall = () => {
    if (typeof setCurrentPage === 'function') setCurrentPage('call-history');
  };

  const handleRefresh = () => setRefreshTick((t) => t + 1);

  const callsSpark = useMemo(
    () => (sparkData && sparkData.length > 1 ? sparkData : undefined),
    [sparkData]
  );

  const urgentDelta = useMemo(() => {
    const callbacks = Number(summary?.callbacks_due ?? 0);
    if (!callbacks) return null;
    return { value: `${callbacks} callback${callbacks === 1 ? '' : 's'}`, tone: 'negative' };
  }, [summary]);

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <PageHeader
        eyebrow="Operations"
        breadcrumb={[{ label: 'Live' }]}
        title="Operations Command Center"
        description="Live operations across calls, leads, appointments and tasks."
        actions={
          <>
            <SubTabBar
              items={[
                { id: 'today', label: 'Today' },
                { id: '7d', label: '7d' },
                { id: '30d', label: '30d' },
              ]}
              value={range}
              onChange={setRange}
            />
            <button
              type="button"
              onClick={handleRefresh}
              className="btn-2050 btn-primary"
            >
              <span className="pulse-dot" style={{ background: '#fff' }} />
              Refresh
            </button>
          </>
        }
      />

      <div className="flex flex-col gap-6">
        <SectionGrid cols={4}>
          <MetricCard
            label="Calls today"
            value={summary?.calls_today ?? 0}
            loading={summaryLoading}
            empty={summaryError}
            sparkline={callsSpark}
            helperText="vs last 7d"
          />
          <MetricCard
            label="Connect rate"
            value={pct == null ? 0 : pct}
            unit="%"
            loading={summaryLoading}
            empty={summaryError}
          />
          <MetricCard
            label="Appointments booked"
            value={summary?.appointments_booked_today ?? 0}
            loading={summaryLoading}
            empty={summaryError}
          />
          <MetricCard
            label="Open urgent tasks"
            value={summary?.open_urgent_tasks ?? 0}
            loading={summaryLoading}
            empty={summaryError}
            delta={urgentDelta}
          />
        </SectionGrid>

        <SectionGrid cols={2}>
          <ChartCard
            title="Call volume"
            description={`Last ${days} day${days === 1 ? '' : 's'} · daily totals`}
            minHeight={300}
          >
            <CallVolumeChart days={days} />
          </ChartCard>
          <ChartCard
            title="Conversion funnel"
            description={`Last ${funnelDays} day${funnelDays === 1 ? '' : 's'}`}
            minHeight={300}
          >
            <FunnelChart days={funnelDays} />
          </ChartCard>
        </SectionGrid>

        <SectionGrid cols={2}>
          <ChartCard
            title="Intent distribution"
            description={`Last ${funnelDays} day${funnelDays === 1 ? '' : 's'}`}
            minHeight={300}
          >
            <IntentChart days={funnelDays} />
          </ChartCard>
          <ChartCard
            title="Objection breakdown"
            description={`Last ${funnelDays} day${funnelDays === 1 ? '' : 's'}`}
            minHeight={300}
          >
            <ObjectionsList days={funnelDays} />
          </ChartCard>
        </SectionGrid>

        <SectionGrid cols={2}>
          <DataCard
            title="Hot leads"
            description="Top-scored recent analyses"
          >
            <HotLeadsPanel onOpenCall={handleOpenCall} />
          </DataCard>
          <DataCard
            title="Urgent tasks"
            description="Open high/urgent items, callbacks first"
          >
            <UrgentTasksPanel />
          </DataCard>
        </SectionGrid>

        <SectionGrid cols={2}>
          <DataCard
            title="Upcoming appointments"
            description="Next 7 days"
          >
            <UpcomingAppointments />
          </DataCard>
          <DataCard
            title="Reminder activity"
            description="Last 7 days"
          >
            <ReminderActivity />
          </DataCard>
        </SectionGrid>
      </div>
    </div>
  );
};

export default Dashboard;
